import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Settings, Download } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { authService } from "@/services/auth.service";
import { endpoints } from "@/config/endpoints";

interface Request {
  _id: string;
  title: string;
  description: string;
  status: string;
  createdAt: string;
  createdBy: {
    name: string;
    email: string;
    phone: string;
  };
}

const OrdersSummary = () => {
  const [requests, setRequests] = useState<Request[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const { token } = authService.getStoredAuth();
        if (!token) {
          throw new Error("No authentication token found");
        }

        const response = await fetch(endpoints.requests.list, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch requests");
        }

        const data = await response.json();
        setRequests(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, []);

  // Function to determine badge color based on status
  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return (
          <Badge className="bg-green-500 hover:bg-green-600 text-white">
            {status}
          </Badge>
        );
      case "pending":
        return (
          <Badge className="bg-blue-400 hover:bg-blue-500 text-white">
            {status}
          </Badge>
        );
      case "cancelled":
        return (
          <Badge className="bg-red-400 hover:bg-red-500 text-white">
            {status}
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="bg-red-400 hover:bg-red-500 text-white">
            {status}
          </Badge>
        );
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-bold">
            Loading requests...
          </CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-red-500">
            Error: {error}
          </CardTitle>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-xl font-bold">Requests Summary</CardTitle>
        <div className="text-gray-400">•••</div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Request ID</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Created By</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request._id} className="border-b">
                  <TableCell className="font-medium">
                    {request._id.slice(-6)}
                  </TableCell>
                  <TableCell>{request.title}</TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">
                        {request.createdBy?.name || "Unknown"}
                      </div>
                      <div className="text-sm text-gray-500">
                        {request.createdBy?.email || "No email"}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {new Date(request.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{getStatusBadge(request.status)}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <button className="p-1 rounded-full hover:bg-gray-200">
                        <Settings size={16} />
                      </button>
                      <button className="p-1 rounded-full hover:bg-gray-200">
                        <Download size={16} />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default OrdersSummary;
